import { intersection, union, uniq } from 'fp-ts/lib/Array';
import { Eq, eqString, getStructEq } from 'fp-ts/lib/Eq';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Spinner, Tab, Tabs } from 'react-bootstrap';
import { ExclamationDiamondFill } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { pathnames } from '../../api';
import { requestItems } from '../../api/meas';
import { getAxiosErrorResponse } from '../../api/utils';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import Customer from '../../models/data/Customer';
import CustomerEquipment from '../../models/data/CustomerEquipment';
import MeasurementDeviceItem from '../../models/data/MeasurementDeviceItem';
import MeasurementItem from '../../models/data/MeasurementItem';
import ItemIdentifier, {
  toItemIdentifierQueryParam,
} from '../../models/requests/ItemIdentifier';
import SelectPanel from '../select-panel';

import styles from './index.module.scss';

const DefaultTabSize = 16;
const eqMeasurementItemKey = getStructEq<MeasurementItem>({
  hostName: eqString,
  positionNo: eqString,
});
const eqMeasurementDeviceItemKey: Eq<MeasurementDeviceItem> = {
  equals: (p1, p2) => p1 === p2 || eqMeasurementItemKey.equals(p1, p2),
};

interface SelectTabState {
  customer?: Customer | null;
  customerEquipment?: CustomerEquipment | null;
  measurementDeviceItems?: MeasurementDeviceItem[] | null;
}

function emptySelectTabStates(tabSize?: number | null) {
  return Array<SelectTabState>(tabSize ?? DefaultTabSize).fill({});
}

function toSelectTabStates(items: MeasurementDeviceItem[]): SelectTabState[] {
  const map = items.reduce((ret, item) => {
    const { customerEquipmentId } = item;
    const itemMap =
      ret.get(customerEquipmentId) ?? new Map<string, MeasurementDeviceItem>();
    itemMap.set(toItemIdentifierQueryParam(item), item);
    ret.set(customerEquipmentId, itemMap);
    return ret;
  }, new Map<number, Map<string, MeasurementDeviceItem>>());
  const results: SelectTabState[] = [];
  map.forEach((value) => {
    const values = Array.from(value.values());
    results.push({
      customer: values[0],
      customerEquipment: values[0],
      measurementDeviceItems: values,
    });
  });
  return results;
}

function toTabKey(index: number): string {
  return `Tab${String(index).padStart(2, '0')}`;
}

export interface SelectTabsProps {
  tabSize?: number;
  defaultItemIdentifiers?: ItemIdentifier[];
  onChange?: (items: MeasurementDeviceItem[]) => void;
}

const SelectTabs: React.FunctionComponent<SelectTabsProps> = ({
  tabSize,
  defaultItemIdentifiers,
  onChange,
}) => {
  const context = useContext(AuthenticatedContext);
  // ---------------------------------------------------------------------
  // Create WEB API URL.
  const history = useHistory();
  const url = history.createHref({ pathname: pathnames.items });
  // ---------------------------------------------------------------------
  // Status
  const [isLoading, setIsLoading] = useState(true);
  const [tabStates, setTabStates] = useState<SelectTabState[]>(
    Array(tabSize ?? DefaultTabSize).fill({}),
  );
  const [
    selectedMeasurementDeviceItems,
    setSelectedMeasurementDeviceItems,
  ] = useState<MeasurementDeviceItem[]>([]);
  // ---------------------------------------------------------------------
  // Callbacks & Functions

  // ---------------------------------------------------------------------
  // Effects
  useEffect(() => {
    let unmounted = false;
    if (defaultItemIdentifiers == null || defaultItemIdentifiers.length <= 0) {
      setTabStates(Array<SelectTabState>(tabSize ?? DefaultTabSize).fill({}));
      setIsLoading(false);
    } else {
      requestItems(url, { item: defaultItemIdentifiers })
        .then(({ data }) => {
          if (!unmounted) {
            const newStates = [
              ...toSelectTabStates(data),
              ...emptySelectTabStates(tabSize),
            ];
            setTabStates(newStates.slice(0, tabSize ?? DefaultTabSize));
            setSelectedMeasurementDeviceItems(data);
          }
        })
        .catch((error) => {
          const res = getAxiosErrorResponse(error);
          toast.error(
            () => {
              return (
                <>
                  <ExclamationDiamondFill />
                  <span>{` 設備項目取得失敗`}</span>
                  <small>{` (${res?.status ?? '応答なし'})`}</small>
                </>
              );
            },
            {
              onClose: () => {
                if (res != null && res.status >= 400 && res.status < 500) {
                  // 認証エラーの可能性大、ログインチェック（期限切れの場合ログイン画面へ遷移する。はず）
                  context.login().catch(() => {
                    // do nothing.
                  });
                }
              },
            },
          );
          if (!unmounted) {
            setTabStates(emptySelectTabStates(tabSize));
            setSelectedMeasurementDeviceItems([]);
          }
        })
        .finally(() => {
          if (!unmounted) {
            setIsLoading(false);
          }
        });
    }
    return () => {
      unmounted = true;
    };
  }, [defaultItemIdentifiers]);
  useEffect(() => {
    const allItems = uniq(eqMeasurementDeviceItemKey)(
      tabStates.flatMap(({ measurementDeviceItems: items }) => items ?? []),
    );
    const exits = intersection(eqMeasurementDeviceItemKey)(
      selectedMeasurementDeviceItems,
      allItems,
    );
    const newItems = union(eqMeasurementDeviceItemKey)(exits, allItems);
    setSelectedMeasurementDeviceItems(newItems);
  }, [tabStates]);
  useEffect(() => {
    if (onChange != null) {
      onChange(selectedMeasurementDeviceItems);
    }
  }, [selectedMeasurementDeviceItems]);
  // ---------------------------------------------------------------------
  // Components
  if (isLoading) {
    return <Spinner animation="border" variant="primary" />;
  }
  return (
    <div className={`${styles.SelectTabs}`}>
      <Tabs>
        {tabStates.map(
          ({ customer, customerEquipment, measurementDeviceItems }, index) => {
            return (
              <Tab
                eventKey={toTabKey(index)}
                title={`${index === 0 ? '設備 ' : ''}${index + 1}`}
              >
                <SelectPanel
                  className={`${styles.SelectPanel}`}
                  selectedCustomer={customer}
                  selectedCustomerEquipment={customerEquipment}
                  selectedMeasurementDeviceItems={measurementDeviceItems}
                  onChange={(newItems, newCustEq, newCust) => {
                    const newTabStates: SelectTabState[] = [
                      ...tabStates.slice(0, index),
                      {
                        customer: newCust,
                        customerEquipment: newCustEq,
                        measurementDeviceItems: newItems,
                      },
                      ...tabStates.slice(index + 1),
                    ];
                    setTabStates(newTabStates);
                  }}
                />
              </Tab>
            );
          },
        )}
      </Tabs>
    </div>
  );
};

SelectTabs.propTypes = {
  tabSize: PropTypes.number,
  defaultItemIdentifiers: PropTypes.arrayOf(
    PropTypes.shape({
      hostName: PropTypes.string.isRequired,
      positionNo: PropTypes.string.isRequired,
    }).isRequired,
  ),
  onChange: PropTypes.func,
};

SelectTabs.defaultProps = {
  tabSize: DefaultTabSize,
  defaultItemIdentifiers: undefined,
  onChange: undefined,
};

export default SelectTabs;
